const ALLOWED_TAGS = new Set(['P', 'BR', 'STRONG', 'EM', 'UL', 'OL', 'LI', 'SPAN']);
const DROP_CONTENT_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'NOSCRIPT', 'TEMPLATE']);

const ALLOWED_INLINE_STYLE_PROPERTIES = new Set(['font-family']);

export function sanitizeInlineStyleAttribute(style: string): string {
  if (!style) return '';

  const safeDeclarations: string[] = [];

  for (const rawDeclaration of style.split(';')) {
    const declaration = rawDeclaration.trim();
    if (!declaration) continue;

    const colonIndex = declaration.indexOf(':');
    if (colonIndex === -1) continue;

    const property = declaration.slice(0, colonIndex).trim().toLowerCase();
    const value = declaration.slice(colonIndex + 1).trim();

    if (!ALLOWED_INLINE_STYLE_PROPERTIES.has(property)) continue;
    if (!value) continue;
    if (/url\s*\(|expression\s*\(|javascript:/i.test(value)) continue;
    if (!/^[a-z0-9,\s"'_-]+$/i.test(value)) continue;

    safeDeclarations.push(`${property}: ${value}`);
  }

  return safeDeclarations.join('; ');
}

function createDocumentForSanitize(html: string): Document | null {
  if (typeof window !== 'undefined' && typeof window.DOMParser !== 'undefined') {
    return new window.DOMParser().parseFromString(html, 'text/html');
  }
  if (typeof DOMParser !== 'undefined') {
    return new DOMParser().parseFromString(html, 'text/html');
  }
  return null;
}

function sanitizeNode(node: Node, outDoc: Document): Node | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return outDoc.createTextNode(node.textContent ?? '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as HTMLElement;
  const tag = element.tagName.toUpperCase();

  if (DROP_CONTENT_TAGS.has(tag)) {
    return null;
  }

  if (!ALLOWED_TAGS.has(tag)) {
    const fragment = outDoc.createDocumentFragment();
    for (const child of Array.from(element.childNodes)) {
      const sanitizedChild = sanitizeNode(child, outDoc);
      if (sanitizedChild) fragment.appendChild(sanitizedChild);
    }
    return fragment;
  }

  const clean = outDoc.createElement(tag.toLowerCase());
  if (tag === 'SPAN') {
    const safeStyle = sanitizeInlineStyleAttribute(element.getAttribute('style') ?? '');
    if (safeStyle) {
      clean.setAttribute('style', safeStyle);
    }
  }
  for (const child of Array.from(element.childNodes)) {
    const sanitizedChild = sanitizeNode(child, outDoc);
    if (sanitizedChild) clean.appendChild(sanitizedChild);
  }
  return clean;
}

export function sanitizeSimpleHtml(html: string): string {
  if (!html) return '';
  const parsed = createDocumentForSanitize(html);
  if (!parsed) {
    return html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  const outDoc = parsed.implementation.createHTMLDocument('');
  const container = outDoc.createElement('div');

  for (const node of Array.from(parsed.body.childNodes)) {
    const sanitized = sanitizeNode(node, outDoc);
    if (sanitized) container.appendChild(sanitized);
  }

  return container.innerHTML;
}

export default sanitizeSimpleHtml;
